const DEFAULTS = {
  maxSizeBytes: 1_000_000,
  maxDimension: 2000,
  mimeType: "image/webp",
  quality: 0.92,
};

export async function compressImage(file: File): Promise<File> {
  const opts = DEFAULTS;

  console.log(`[compressImage] input "${file.name}": ${sizeFormatter(file.size)} (${file.type}), target ≤ ${sizeFormatter(opts.maxSizeBytes)}`);

  if (!file.type.startsWith("image/")) {
    throw new Error("compressImage: file is not an image");
  }

  if (file.size <= opts.maxSizeBytes) {
    console.log(`[compressImage] already under target — returning original, no compression done`);
    return file;
  }

  const bitmap = await loadBitmap(file);
  console.log(`[compressImage] decoded dimensions: ${bitmap.width}x${bitmap.height}`);

  const {width, height} = clampDimensions(bitmap.width, bitmap.height, opts.maxDimension);

  if (width !== bitmap.width || height !== bitmap.height) {
    console.log(`[compressImage] over maxDimension (${opts.maxDimension}px) — capping to ${width}x${height}`);
  } else {
    console.log(`[compressImage] within maxDimension — no resizing needed`);
  }

  console.log(`[compressImage] encoding at ${width}x${height}, quality ${opts.quality.toFixed(2)}`);
  const blob = await encodeAtSize(bitmap, width, height, opts.mimeType, opts.quality);

  bitmap.close?.();

  if (!blob) {
    console.error(`[compressImage] canvas encoding failed`);
    throw new Error("compressImage: canvas encoding failed");
  }

  const newName = replaceExtension(file.name, "webp");
  const savings = (100 * (1 - blob.size / file.size)).toFixed(0);

  console.log(`[compressImage] result: "${newName}" — ${sizeFormatter(file.size)} -> ${sizeFormatter(blob.size)} (${savings}% smaller)`);

  if (blob.size > opts.maxSizeBytes) {
    console.warn(`[compressImage] still above target (${sizeFormatter(blob.size)} > ${sizeFormatter(opts.maxSizeBytes)}) after downscale + WebP conversion`);
  }

  return new File([blob], newName, {type: opts.mimeType, lastModified: Date.now()});
}

async function loadBitmap(file: File): Promise<ImageBitmap> {
  if ("createImageBitmap" in window) {
    return await createImageBitmap(file);
  }
  const url = URL.createObjectURL(file);

  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = url;
    });

    return img as unknown as ImageBitmap;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function clampDimensions(width: number, height: number, maxDimension: number): { width: number; height: number } {
  if (width <= maxDimension && height <= maxDimension) {
    return {width, height};
  }

  const scale = maxDimension / Math.max(width, height);
  return {width: Math.round(width * scale), height: Math.round(height * scale)};
}

function encodeAtSize(bitmap: ImageBitmap | HTMLImageElement, width: number, height: number, mimeType: string, quality: number): Promise<Blob | null> {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext("2d");
  if (!ctx) return Promise.resolve(null);

  ctx.drawImage(bitmap as CanvasImageSource, 0, 0, width, height);

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), mimeType, quality);
  });
}

function replaceExtension(filename: string, ext: string): string {
  const dot = filename.lastIndexOf(".");
  const base = dot === -1 ? filename : filename.slice(0, dot);
  return `${base}.${ext}`;
}

function sizeFormatter(bytes: number): string {
  return `${(bytes / 1024).toFixed(1)}KB`;
}